import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  RefreshControl,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { COLORS, FONTS } from '../../constants/config';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks';
import { PageContainer, Breadcrumb } from '../../components';
import { emiratePlanApi, EmiratePlanGoal } from '../../services/apiService';

/**
 * خطة إمارة الفجيرة — DTS UC-02 step 3.
 *
 * Reference catalog. Strategy Team adds plan goals here so other goal
 * authors can optionally align their strategic goals to one of these.
 */
export default function EmiratePlanScreen() {
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  const canEdit = !!user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.strategic_goals.create');

  const [items, setItems] = useState<EmiratePlanGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [editing, setEditing] = useState<EmiratePlanGoal | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCode, setFormCode] = useState('');
  const [formYear, setFormYear] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const resp = await emiratePlanApi.list({ page_size: 200 });
      setItems(resp.results || []);
    } catch (err) {
      console.error('Failed to load emirate plan goals:', err);
      showError('تعذّر تحميل خطة الإمارة');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showError]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));

  const openAdd = () => {
    setEditing(null);
    setFormTitle('');
    setFormDescription('');
    setFormCode('');
    setFormYear('');
    setShowForm(true);
  };

  const openEdit = (item: EmiratePlanGoal) => {
    setEditing(item);
    setFormTitle(item.title);
    setFormDescription(item.description || '');
    setFormCode(item.code || '');
    setFormYear(item.year ? String(item.year) : '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formTitle.trim()) {
      showError('عنوان الهدف إجباري');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        title: formTitle.trim(),
        description: formDescription.trim(),
        code: formCode.trim(),
        year: formYear ? parseInt(formYear, 10) : null,
        is_active: true,
      };
      if (editing) {
        await emiratePlanApi.update(editing.id, payload);
        showSuccess('تم تحديث الهدف');
      } else {
        await emiratePlanApi.create(payload);
        showSuccess('تم إضافة الهدف');
      }
      setShowForm(false);
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: EmiratePlanGoal) => {
    if (!window.confirm(`حذف "${item.title}"؟`)) return;
    try {
      await emiratePlanApi.delete(item.id);
      showSuccess('تم الحذف');
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الحذف');
    }
  };

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    inputBackground: colors.inputBackground,
    border: colors.border,
  };

  return (
    <PageContainer>
      <ScrollView
        contentContainerStyle={{ padding: 16, gap: 16 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData(); }} />
        }
      >
        <Breadcrumb items={[{ label: 'الرئيسية', icon: 'home' as any }, { label: 'خطة إمارة الفجيرة' }]} />

        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.title, { color: themeColors.textPrimary }]}>خطة إمارة الفجيرة</Text>
            <Text style={[styles.subtitle, { color: themeColors.textMuted }]}>
              يُدخل فريق الاستراتيجية أهداف خطة الإمارة هنا لربطها لاحقا بالأهداف الاستراتيجية للدائرة (اختياري).
            </Text>
          </View>
          {canEdit && (
            <TouchableOpacity style={styles.addButton} onPress={openAdd}>
              <Text style={styles.addButtonText}>+ إضافة هدف</Text>
            </TouchableOpacity>
          )}
        </View>

        {loading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : items.length === 0 ? (
          <View style={[styles.emptyCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>
              لا توجد أهداف لخطة الإمارة بعد
            </Text>
            {canEdit && (
              <Text style={[styles.emptyHint, { color: themeColors.textMuted }]}>
                ابدأ بإضافة هدف من زر "إضافة هدف" أعلاه.
              </Text>
            )}
          </View>
        ) : (
          <View style={{ gap: 12 }}>
            {items.map((item) => (
              <View key={item.id} style={[styles.itemCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row-reverse', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {item.code ? (
                      <View style={styles.codeBadge}>
                        <Text style={styles.codeBadgeText}>{item.code}</Text>
                      </View>
                    ) : null}
                    <Text style={[styles.itemTitle, { color: themeColors.textPrimary }]}>{item.title}</Text>
                    {item.year ? (
                      <Text style={[styles.itemMeta, { color: themeColors.textMuted }]}>{item.year}</Text>
                    ) : null}
                  </View>
                  {item.description ? (
                    <Text style={[styles.itemDescription, { color: themeColors.textMuted }]}>{item.description}</Text>
                  ) : null}
                  <Text style={[styles.alignedCount, { color: themeColors.textMuted }]}>
                    عدد الأهداف الاستراتيجية المرتبطة: {item.aligned_count ?? 0}
                  </Text>
                </View>
                {canEdit && (
                  <View style={{ flexDirection: 'row-reverse', gap: 8 }}>
                    <TouchableOpacity style={styles.editBtn} onPress={() => openEdit(item)}>
                      <Text style={styles.editBtnText}>تعديل</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.deleteBtn} onPress={() => handleDelete(item)}>
                      <Text style={styles.deleteBtnText}>حذف</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <Modal visible={showForm} transparent animationType="fade" onRequestClose={() => setShowForm(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>
              {editing ? 'تعديل هدف خطة الإمارة' : 'إضافة هدف خطة الإمارة'}
            </Text>
            <View style={{ gap: 12 }}>
              <View>
                <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>العنوان *</Text>
                <TextInput
                  value={formTitle}
                  onChangeText={setFormTitle}
                  placeholder="عنوان الهدف"
                  placeholderTextColor={themeColors.textMuted}
                  style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                  textAlign="right"
                />
              </View>
              <View>
                <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الوصف</Text>
                <TextInput
                  value={formDescription}
                  onChangeText={setFormDescription}
                  placeholder="وصف الهدف"
                  placeholderTextColor={themeColors.textMuted}
                  style={[styles.input, styles.textarea, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                  textAlign="right"
                  multiline
                />
              </View>
              <View style={{ flexDirection: 'row-reverse', gap: 12 }}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الرمز</Text>
                  <TextInput
                    value={formCode}
                    onChangeText={setFormCode}
                    placeholder="EP-2026-1"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>السنة</Text>
                  <TextInput
                    value={formYear}
                    onChangeText={(v) => setFormYear(v.replace(/\D/g, ''))}
                    placeholder="2026"
                    placeholderTextColor={themeColors.textMuted}
                    keyboardType="numeric"
                    style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                  />
                </View>
              </View>
            </View>
            <View style={{ flexDirection: 'row-reverse', gap: 8, marginTop: 16 }}>
              <TouchableOpacity
                style={[styles.modalSaveBtn, saving && { opacity: 0.5 }]}
                disabled={saving}
                onPress={handleSave}
              >
                <Text style={styles.modalSaveBtnText}>{saving ? '...' : 'حفظ'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalCancelBtn, { borderColor: themeColors.border }]}
                onPress={() => setShowForm(false)}
              >
                <Text style={[styles.modalCancelBtnText, { color: themeColors.textPrimary }]}>إلغاء</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  headerCard: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  title: { fontSize: 20, fontFamily: FONTS.medium, textAlign: 'right' },
  subtitle: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginTop: 4 },
  addButton: {
    paddingVertical: 10, paddingHorizontal: 16,
    backgroundColor: COLORS.primary, borderRadius: 8,
  },
  addButtonText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  emptyCard: {
    padding: 40, borderRadius: 12, borderWidth: 1, alignItems: 'center',
  },
  emptyText: { fontSize: 16, fontFamily: FONTS.regular },
  emptyHint: { fontSize: 13, marginTop: 8, fontFamily: FONTS.regular },
  itemCard: {
    flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 12,
    padding: 16, borderRadius: 12, borderWidth: 1,
  },
  itemTitle: { fontSize: 16, fontFamily: FONTS.medium, textAlign: 'right' },
  itemDescription: { fontSize: 13, marginTop: 6, textAlign: 'right' },
  itemMeta: { fontSize: 12, fontFamily: FONTS.regular },
  alignedCount: { fontSize: 12, marginTop: 8, textAlign: 'right' },
  codeBadge: {
    paddingVertical: 2, paddingHorizontal: 8,
    backgroundColor: '#EFF6FF', borderRadius: 6,
  },
  codeBadgeText: { color: '#1E40AF', fontSize: 12, fontFamily: FONTS.medium },
  editBtn: {
    paddingVertical: 6, paddingHorizontal: 12,
    backgroundColor: '#EFF6FF', borderRadius: 6,
  },
  editBtnText: { color: '#1E40AF', fontSize: 13, fontFamily: FONTS.medium },
  deleteBtn: {
    paddingVertical: 6, paddingHorizontal: 12,
    backgroundColor: '#FEF2F2', borderRadius: 6,
  },
  deleteBtnText: { color: '#B91C1C', fontSize: 13, fontFamily: FONTS.medium },
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    width: '100%', maxWidth: 560, padding: 20, borderRadius: 12, borderWidth: 1,
  },
  modalTitle: {
    fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right', marginBottom: 16,
  },
  formLabel: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginBottom: 6 },
  input: {
    borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 14, fontFamily: FONTS.regular, minHeight: 44,
  },
  textarea: { minHeight: 90, paddingTop: 10 },
  modalSaveBtn: {
    flex: 1, paddingVertical: 12, backgroundColor: COLORS.primary,
    borderRadius: 8, alignItems: 'center',
  },
  modalSaveBtnText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  modalCancelBtn: {
    flex: 1, paddingVertical: 12, borderWidth: 1,
    borderRadius: 8, alignItems: 'center',
  },
  modalCancelBtnText: { fontSize: 14, fontFamily: FONTS.medium },
});
